import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import { toastSuccess, toastError, errorMessage } from '../../../lib/toast';
import { formatDateTime } from '../../../lib/formatDate';
import IntegrationStatusPill from './IntegrationStatusPill';
import './integrations.css';

const SES_ERR = {
  ses_not_configured: 'SES is not configured.',
  ses_sender_unverified: 'The sender address is not verified in SES.',
  ses_access_denied: 'SES access denied — check IAM permissions.',
  ses_error: 'SES error. Try again.',
  bad_template: 'Unknown template.',
};

const SERVICES = [
  { key: 'odoo', label: 'Odoo', desc: 'HR & department sync' },
  { key: 'ssm', label: 'Parameter Store', desc: 'App secrets & references' },
  { key: 'ses', label: 'SES', desc: 'Transactional email & digest' },
];

export default function IntegrationsPanel() {
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState('');
  const [sending, setSending] = useState(false);
  const [checkedAt, setCheckedAt] = useState({}); // { [svc]: Date }

  useEffect(() => {
    let active = true;
    axios.get(`${API_BASE_URL}/api/admin/integrations`, { withCredentials: true })
      .then((r) => { if (active) setConfig(r.data); })
      .catch(() => { if (active) setError(true); });
    axios.get(`${API_BASE_URL}/api/admin/email/templates`, { withCredentials: true })
      .then((r) => { if (active) { const t = r.data.templates || []; setTemplates(t); if (t[0]) setTemplate(t[0].key); } })
      .catch(() => { /* template list optional */ });
    return () => { active = false; };
  }, []);

  // Wrap the probe so we stamp a client-side "last checked" time on resolve,
  // then pass the {ok,reason} result through to the pill unchanged.
  const testService = (service) => async () => {
    const r = await axios.post(`${API_BASE_URL}/api/admin/integrations/${service}/test`, {}, { withCredentials: true });
    setCheckedAt((m) => ({ ...m, [service]: new Date() }));
    return r.data;
  };

  const sendTest = async () => {
    if (!template) return;
    setSending(true);
    try {
      await axios.post(`${API_BASE_URL}/api/admin/email/test`, { template }, { withCredentials: true });
      toastSuccess('Test email sent to your address.');
    } catch (err) {
      toastError(errorMessage(err, 'Could not send the test email.', SES_ERR));
    } finally {
      setSending(false);
    }
  };

  if (error) return <section className="integ-panel"><p className="integ-state">Could not load integration status.</p></section>;
  if (!config) return <section className="integ-panel"><p className="integ-state">Loading integrations…</p></section>;

  return (
    <section className="integ-panel">
      <p className="set-intro">Check connectivity to external systems and send a test email.</p>
      <ul className="integ-list">
        {SERVICES.map(({ key, label, desc }) => {
          const configured = !!config[key]?.configured;
          return (
            <li key={key} className="integ-card">
              <div className="integ-card__head">
                <h3 className="integ-card__title">{label}</h3>
                <p className="integ-card__desc">{desc}</p>
              </div>
              <IntegrationStatusPill label={label} configured={configured} onTest={testService(key)} />
              {checkedAt[key] && (
                <p className="integ-last-checked">Last checked: {formatDateTime(checkedAt[key])}</p>
              )}
              {key === 'ses' && configured && templates.length > 0 && (
                <div className="integ-email-card">
                  <span className="integ-email-card__title">Send a test email</span>
                  <div className="integ-email-test">
                    <label htmlFor="integ-tpl" className="integ-state">Template:</label>
                    <select id="integ-tpl" value={template} onChange={(e) => setTemplate(e.target.value)}>
                      {templates.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                    </select>
                    <button type="button" className="integ-test-btn" onClick={sendTest} disabled={sending}>
                      {sending ? 'Sending…' : 'Send test to me'}
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
